import { MouseEvent, useContext } from 'react'

import { Heading, Icon } from '@hhgtech/hhg-components'
import { AspectRatio, Box, BoxProps, Button, Tooltip } from '@mantine/core'
import { useIntl } from 'react-intl'

import { Disclaimer } from 'components/atoms/disclaimer'
import { NextImageWrap } from 'components/atoms/imageWrap'
import { CarePathDefaultContext } from 'components/templates/carePath/landing/context'
import { useGetEncodedGaId } from 'hooks/useGetClientGaId'
import { useHandlerUrls } from 'hooks/useHandlerUrls'
import { useIsMobile } from 'hooks/useIsMobile'
import { DataTrackingTypeHTMLElement, HeroBannerItem } from 'interfaces/types'
import { formatUrlWithEncrypedGa } from 'utils/url'

import useStyles from './index.styles'
import { HeroBannerContext } from '../../context'

export type BannerItemProps = {
  i: HeroBannerItem
  ratio?: number
  dataTracking?: DataTrackingTypeHTMLElement
  listBannerLength: number
  keepRatio: boolean
} & BoxProps

export const Item = ({
  styles,
  ratio,
  classNames,
  dataTracking,
  keepRatio = true,
  i,
  listBannerLength,
}: BannerItemProps) => {
  const { formatMessage: f } = useIntl()
  const { detectPage } = useContext(CarePathDefaultContext)
  const { isCarePath } = detectPage
  const { classes, cx, theme } = useStyles(
    { listBannerLength },
    {
      name: 'CarePathHeroBannerBannerItem',
      styles,
      classNames,
    },
  )
  const { onItemClick } = useContext(HeroBannerContext)
  const isMobile = useIsMobile()

  const linkData = useHandlerUrls([i.linkTo])
  const { encodedGaId } = useGetEncodedGaId()

  const insideEle = (i: HeroBannerItem) => {
    const { isValidUrl, formatUrl, isExternalUrl, isExternalSite } =
      linkData[0] || {}
    const formatUrlResult = isCarePath
      ? formatUrlWithEncrypedGa(formatUrl, encodedGaId)
      : formatUrl
    const trackingData = {
      className: 'pointer-event-child-none',
      'data-event-category': 'Service Connection',
      'data-event-action': 'Banner Click',
      'data-event-label': formatUrlResult,
      ...dataTracking,
    }
    const boxWrapper: any = {
      component: isValidUrl ? 'a' : 'div',
      href: formatUrlResult,
      target: isExternalUrl ? '_blank' : '_self',
      ...trackingData,
      onClick: (e: MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
        if (!isExternalUrl && formatUrlResult?.includes('#')) {
          e.preventDefault()
          const splitArr = formatUrlResult.split('#')
          onItemClick?.(splitArr[splitArr.length - 1])
        }
      },
    }

    return (
      <Box {...(!i.ctaTitle && boxWrapper)}>
        {i.title && (
          <Heading className={classes.heading} tag="h1">
            {i.title}
          </Heading>
        )}
        {i.ctaTitle && (
          <Box className={cx(classes.cta)}>
            <Tooltip
              arrowPosition="center"
              arrowSize={8}
              width={264}
              withinPortal
              withArrow
              label={
                <Disclaimer
                  classNames={{
                    root: classes.disclaimer,
                    content: classes.disclaimerText,
                  }}
                  content={f({
                    id: 'confirmLeavePage.content',
                    defaultMessage:
                      'As your trusted health partner, we want to ensure you have the information you need to safeguard your health. Are you sure you want to leave this page?',
                  })}
                />
              }
              position="bottom"
              disabled={!(isExternalSite && i.ctaTitle)}
              styles={{
                tooltip: {
                  padding: 0,
                  whiteSpace: 'normal',
                  background: theme.colors.yellow[0],
                },
              }}
            >
              <Button
                {...trackingData}
                data-primary-btn="true"
                classNames={{
                  root: classes.ctaBtn,
                  rightIcon: classes.ctaBtnRightIcon,
                }}
                size="lg"
                rightIcon={
                  <Icon.ArrowRightTail
                    className={classes.ctaArrow}
                    color="currentColor"
                    size={isMobile ? 20 : 24}
                  />
                }
                onClick={(e: any) => {
                  if (!isExternalUrl) {
                    e.preventDefault()
                    if (formatUrlResult.includes('#')) {
                      const splitArr = formatUrlResult.split('#')
                      onItemClick?.(splitArr[splitArr.length - 1])
                    } else {
                      window.location.href = formatUrlResult
                    }
                  } else {
                    window.open(formatUrlResult)
                  }
                }}
              >
                {i.ctaTitle}
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <>
      {keepRatio ? (
        <AspectRatio ratio={ratio || (isMobile ? 375 / 500 : 1920 / 600)}>
          <NextImageWrap
            src={isMobile ? i.mobileBanner : i.desktopBanner}
            fill
            alt={i.title}
          />
          {insideEle(i)}
        </AspectRatio>
      ) : (
        <>
          <img
            loading="eager"
            src={isMobile ? i.mobileBanner : i.desktopBanner}
            alt=""
          />
          {insideEle(i)}
        </>
      )}
    </>
  )
}
